<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { BucketUserAssociationCreateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { SHARING_USER_ROLES, SharingModeSelectOptions } from '@/constants';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  bucketId: number;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

const show = computed<boolean>(() => {
  return props.show;
});

// --- Component state ---
const userEmail = ref('');
const userRole = ref(SHARING_USER_ROLES.VIEWER);
const description = ref('');

// --- Mutation: Create Project-User Association ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(BucketUserAssociationCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.bucketUserAssociationCreate) {
    console.debug(
      'Bucket-user association created:',
      res.data.bucketUserAssociationCreate
    );
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Bucket-user association failed: ' + res.data;
    console.error('Bucket-user association failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error creating bucket-user association:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onBucketUserAssociationRequested() {
  if (!userEmail.value) {
    mutationError.value = 'User email not provided';
    console.error('User email not provided');
    return;
  }
  console.debug(
    'Creating bucket-user association, project ',
    props.bucketId,
    'user',
    userEmail.value
  );

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    bucketId: props.bucketId,
    userEmail: userEmail.value,
    userRole: userRole.value,
    description: description.value,
  });
}

function closeModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Create Project-User Association -->
    <b-modal v-model="show" @hide="closeModal">
      <!-- Modal Title -->
      <template #title>
        <font-awesome-icon icon="plus" class="me-2" />
        Share data
      </template>
      <!-- Modal Content -->
      <div>
        <BFormGroup label="User e-mail" label-for="user-email" class="mt-3">
          <BFormInput
            v-model="userEmail"
            id="user-email"
            name="user-email"
            placeholder="Enter user e-mail"
            required
          ></BFormInput>
        </BFormGroup>

        <BFormGroup label="User role" label-for="user-role" class="mt-3">
          <b-form-select
            id="user-role"
            v-model="userRole"
            :options="SharingModeSelectOptions"
            class="form-control mb-2"
          />
        </BFormGroup>

        <BFormGroup label="Description" label-for="description" class="mt-3">
          <b-form-textarea
            id="description"
            v-model="description"
            class="form-control mb-2"
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
          </b-form-textarea>
        </BFormGroup>

        <!-- Error Mutation -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Modal Footer -->
      <template #footer>
        <b-button variant="secondary" @click="closeModal"> Close </b-button>
        <b-button
          variant="primary"
          :disabled="mutationInProgress || mutationShowSuccess"
          @click="onBucketUserAssociationRequested"
        >
          <span v-if="mutationInProgress">
            Sharing Data
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Sharing Successful
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Share Data </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
